import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PermanentDeleteDialog({ driver, open, onClose }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  const full = driver ? `${driver.first_name || driver.name?.split(" ")[0] || ""} ${driver.last_name || driver.name?.split(" ").slice(1).join(" ") || ""}`.trim() : "";
  const ready = name.trim().toLowerCase() === full.toLowerCase() && (!isAdmin || pin.length >= 4);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/drivers/${driver.id}/purge`, { confirm_name: name, pin: isAdmin ? pin : undefined });
      toast.success(`${full} permanently deleted` + (Object.keys(data.deleted || {}).length ? ` (${Object.entries(data.deleted).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(", ")})` : ""));
      setName(""); setPin(""); onClose(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="permanent-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" />Permanently delete {full}</DialogTitle>
          <DialogDescription>For drivers added by mistake only. This removes the driver and every linked record — tests, certificates, reports, documents and violations — with no retention copy. It cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="purge-name">Type the driver's full name to confirm</Label><Input id="purge-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={full} autoComplete="off" data-testid="purge-name-input" /></div>
          {isAdmin && <div><Label htmlFor="purge-pin">Master PIN</Label><Input id="purge-pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} autoComplete="off" data-testid="purge-pin-input" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="purge-cancel">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700" disabled={busy || !ready} onClick={submit} data-testid="purge-confirm">{busy ? "Deleting…" : "Delete permanently"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
